import React, { useState, useEffect, useContext } from 'react';
import { Context } from '../../Context';
import { Header, Container, List, Loader, Button, Input } from 'semantic-ui-react';
import { getCurrencyEvents, incrementBalance, decrementBalance } from '../../api';
import BalanceTable from './BalanceTable';
import CurrencyGraph from './CurrencyGraph';
import SweetPagination from 'sweetpagination';
import { useAuthorization } from '../../Contexts/AuthorizationContext';

function UserBalance() {
    // --- Context and Auth ---
    let { user } = useAuthorization();
    const { game, player } = useContext(Context);

    // --- State ---
    const [isLoadingEvents, setIsLoadingEvents] = useState(false);
    const [isLoadingAction, setIsLoadingAction] = useState({}); // Loading state per currency action
    const [events, setEvents] = useState([]);
    const [types, setTypes] = useState({});
    const [amounts, setAmounts] = useState({}); // State object for input amounts { currencyType: amount }
    const [displayCurrencies, setDisplayCurrencies] = useState([]); // Local state for displaying/updating balances

    // PAGINATION
    const [currentPageData, setCurrentPageData] = useState([]);

    // --- Effects ---

    // Effect to fetch currency events on mount or when game/player changes
    useEffect(() => {
        if (!game || !player?.guid) return; // Ensure game and player guid exist

        setIsLoadingEvents(true);
        getCurrencyEvents(game, player.guid, (err, data) => {
            setIsLoadingEvents(false);
            if (err) {
                console.error("Error fetching currency events:", err);
                setEvents([]);
                setTypes({});
                return;
            }
            if (data) {
                data.events.sort((a, b) => b.timestamp - a.timestamp);
                setEvents(data.events || []);
                setTypes(data.types || {});
            } else {
                 setEvents([]);
                 setTypes({});
            }
        });
    }, [game, player?.guid]); // Depend on game and player.guid

    // Effect to sync displayCurrencies with player context and initialize amounts
    useEffect(() => {
        if (player && player.currencies) {
            const currencyArray = Object.values(player.currencies);
            setDisplayCurrencies(currencyArray);

            // Initialize amounts state if not already set for these currencies
            setAmounts(prevAmounts => {
                const newAmounts = { ...prevAmounts };
                currencyArray.forEach(currency => {
                    if (!(currency.type in newAmounts)) {
                        newAmounts[currency.type] = 0; // Initialize with 0 or ''
                    }
                });
                return newAmounts;
            });

        } else {
            setDisplayCurrencies([]);
            setAmounts({}); // Clear amounts if no player/currencies
        }
    }, [player]); // Depend only on player object

    // --- Development User ---
    // Be cautious with this in production builds
    if (process.env.NODE_ENV === 'development' && !user?.guid) {
        // console.log('Nessun utente loggato (development) verrà creato uno user di sviluppo');
        user = {
            guid: 'development-guid',
            authorization: 'admin', // Or appropriate level for testing
        };
    }

    // --- Event Handlers ---

    const handleAmountChange = (currencyType, value) => {
        // Ensure value is a non-negative number
        const numericValue = Math.max(0, Number(value) || 0);
        setAmounts(prevAmounts => ({
            ...prevAmounts,
            [currencyType]: numericValue,
        }));
    };

    const updateCurrencyBalance = (currencyType, newBalance) => {
        setDisplayCurrencies(prevCurrencies =>
            prevCurrencies.map(currency =>
                currency.type === currencyType
                    ? { ...currency, balance: newBalance }
                    : currency
            )
        );
        // Reset the input amount for the specific currency after success
        handleAmountChange(currencyType, 0);
    };

    const setActionLoading = (currencyType, isLoading) => {
         setIsLoadingAction(prev => ({...prev, [currencyType]: isLoading }));
    }

    const handleIncrement = (currencyType) => {
        const amountToIncrement = amounts[currencyType] || 0;
        if (amountToIncrement <= 0) {
             console.warn("Increment amount must be positive.");
             return; // Don't proceed if amount is zero or negative
        }
        if (!user?.guid || !user?.authorization) {
            console.error("User not authorized for increment.");
            return;
        }
        if (!game || !player?.guid) {
             console.error("Game or Player GUID missing for increment.");
             return;
        }

        setActionLoading(currencyType, true);
        incrementBalance(
            user.guid,
            user.authorization,
            game,
            player.guid,
            currencyType,
            amountToIncrement,
            (err, updatedBalance) => {
                setActionLoading(currencyType, false);
                if (!err && updatedBalance?.balance !== undefined) {
                    console.log('Increment successful:', updatedBalance);
                    updateCurrencyBalance(currencyType, updatedBalance.balance);
                } else {
                    console.error('Increment failed:', err || 'No updated balance received');
                    // Optionally: Show an error message to the user
                }
            }
        );
    };

    const handleDecrement = (currencyType) => {
        const amountToDecrement = amounts[currencyType] || 0;
         if (amountToDecrement <= 0) {
             console.warn("Decrement amount must be positive.");
             return; // Don't proceed if amount is zero or negative
        }
       if (!user?.guid || !user?.authorization) {
            console.error("User not authorized for decrement.");
            return;
        }
        if (!game || !player?.guid) {
             console.error("Game or Player GUID missing for decrement.");
             return;
        }

        setActionLoading(currencyType, true);
        decrementBalance(
            user.guid,
            user.authorization,
            game,
            player.guid,
            currencyType,
            amountToDecrement,
            (err, updatedBalance) => {
                setActionLoading(currencyType, false);
                if (!err && updatedBalance?.balance !== undefined) {
                    console.log('Decrement successful:', updatedBalance);
                    updateCurrencyBalance(currencyType, updatedBalance.balance);
                } else {
                    console.error('Decrement failed:', err || 'No updated balance received');
                     // Optionally: Show an error message to the user
                }
            }
        );
    };

    // --- Render ---

    if (!player) return <Container><Header>User Balance</Header><div>Loading player data or player not found...</div></Container>;
    // Added check for user in non-dev environments
    if (!user?.guid && process.env.NODE_ENV !== 'development') return <Container><Header>User Balance</Header><div>User not logged in.</div></Container>;


    return (
        <Container>
            <Header>User Balance</Header>
            {/* Optional: Render graphs only if data exists */}
            {events.length > 0 && <CurrencyGraph data={events} select='SFT'/>}
            {events.length > 0 && <CurrencyGraph data={events} select='HRD'/>}

            <List divided relaxed verticalAlign="middle" style={{ marginBottom: '2em' }}>
                {displayCurrencies.length === 0 && <List.Item><div>No currencies found for this user.</div></List.Item>}
                {displayCurrencies.map(currency => {
                    const isLoading = isLoadingAction[currency.type] || false;
                    return (
                        <List.Item key={currency.type} style={{ marginTop: '10px', padding: '10px 0' }}>
                            <List.Content floated='right'>
                                {/* Buttons and Input */}
                                <Input
                                    type="number"
                                    min="0" // Prevent negative numbers directly in input
                                    placeholder="Amount"
                                    value={amounts[currency.type] || ''} // Use '' for empty display instead of 0
                                    onChange={(e) => handleAmountChange(currency.type, e.target.value)}
                                    style={{ marginRight: '10px', width: '100px' }}
                                    disabled={isLoading} // Disable input while action is loading
                                />
                                <Button
                                    size="small"
                                    color="green"
                                    onClick={() => handleIncrement(currency.type)}
                                    style={{ marginRight: '5px' }}
                                    disabled={isLoading || (amounts[currency.type] || 0) <= 0} // Disable if loading or amount <= 0
                                    loading={isLoading}
                                >
                                    Inc
                                </Button>
                                <Button
                                    size="small"
                                    color="red"
                                    onClick={() => handleDecrement(currency.type)}
                                     disabled={isLoading || (amounts[currency.type] || 0) <= 0 || currency.balance < (amounts[currency.type] || 0)} // Disable if loading, amount <= 0, or insufficient balance
                                     loading={isLoading}
                                >
                                    Dec
                                </Button>
                            </List.Content>
                            <List.Content>
                                <List.Header>{currency.type}</List.Header>
                                <List.Description style={{ marginTop: '5px' }}>
                                    Balance: {currency.balance}
                                </List.Description>
                            </List.Content>
                        </List.Item>
                    )
                })}
            </List>

            <Header>Currency Events</Header>
            {isLoadingEvents ? (
                <Loader active inline='centered' />
            ) : (
                events.length > 0 ? (
                    <div>
                        <BalanceTable events={currentPageData} types={types} />
                        <SweetPagination
                            currentPageData={setCurrentPageData}
                            getData={events}
                            dataPerPage={30}
                            navigation={true}
                            getStyle={'style-1'} // Example style
                        />
                    </div>
                ) : (
                     <div>No currency events found.</div>
                )
            )}
        </Container>
    );
}
export default UserBalance;